
import { mapGetters, mapState } from 'vuex';
import axios from 'axios';
import gql from 'graphql-tag';
import { actionForceOpen, actionForceCancel, actionUpdateMetadata } from '@/api/action';
import Logging from '@/logging';
import RsltAgGrid from '@/components/RsltAgGrid/index.vue';
import { parseTime } from '@/utils';
import Utils from '@/utils/utils';
import { getCustomFieldLabel, getCustomFieldValue, formatCustomFieldValue, getAgGridFilterType } from '@/components/_CustomFields/helpers';
import AttachmentsDialog from '@/components/_dialogs/AttachmentsDialog.vue';
import { S3_Attachment } from '@/schema/attachment';
import { IWorklistState } from '@/store/modules/worklist';

const GET_USER_INTERFACE_DETAILS = gql`
	query WorklistActionTable_userInterfaceDetails($participant: uuid!) {
		userInterfaceDetails: risolto_participant(
			where: { reference: { _eq: $participant } }
		) {
			custom_field_definitions: user_interface_details(path: "action.custom_field_definitions")
			disallowed_channels: user_interface_details(path: "action.disallowed_channels")
			disallowed_message_codes: user_interface_details(path: "action.disallowed_message_codes")
		}
	}
`;

export default {
	components: {
		RsltAgGrid,
		AttachmentsDialog
	},
	apollo: {
		userInterfaceDetails: {
			query: GET_USER_INTERFACE_DETAILS,
			variables() {
				return { participant: this.selectedParticipantStore };
			},
			update: data => data.userInterfaceDetails[0]
		}
	},
	data() {
		return {
			selectedActions: [],
			loading: false,
			executing: false,
			STATUS_FILTERS: [{ value: 'OPEN' }, { value: 'DONE' }, { value: 'CANCELED' }, { value: 'DRAFT' }],
			gridApi: null,
			initialRowData: [] as any[],
			columnDefs: [] as any[],
			userInterfaceDetails: {
				custom_field_definitions: [],
				disallowed_channels: [],
				disallowed_message_codes: []
			},
			statusFilter: [] as string[],
			assigneeFilter: null as string | null,
			dateRange: undefined as string[] | undefined,
			groupVars: {} as any,
			limit: 10000,
			maxLimit: 100000,
			showLimitWarning: false,
			actualCount: 0,
			attachmentsDialogVisible: false,
			attachmentsDialogAction: null,
			attachmentsDialogFetching: false,
			sideBar: {
				toolPanels: ['columns', 'filters']
			},
			statusBar: {
				statusPanels: [
					{ statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
					{ statusPanel: 'agSelectedRowCountComponent', align: 'left' }
				]
			}
		};
	},
	computed: {
		...mapGetters([
			'env',
			'selectedParticipantStore',
			'selectedLegalEntityStore',
			'participantUsers'
		]),
		...mapState('worklist', {
			worklistGroupVars: (state: IWorklistState) => state.worklistGroupVars,
			worklistDateRange: (state: IWorklistState) => state.worklistDateRange,
			worklistStatusState: (state: IWorklistState) => state.worklistStatusState,
			worklistExecutionType: (state: IWorklistState) => state.worklistExecutionType,
			worklistAssignee: (state: IWorklistState) => state.worklistAssignee
		}),
		availableActions() {
			return this.$store.getters['worklist/worklistActions'];
		},
		assigneeOptions() {
			if (!this.participantUsers?.length) return [];
			return this.participantUsers.map(u => ({
				value: u.person_by_reference.reference,
				label: u.person_by_reference.username || u.person_by_reference.reference
			}));
		},
		attachmentsDialogFileList() {
			if (!this.attachmentsDialogAction) return [];
			return this.getCombinedAttachments(this.attachmentsDialogAction);
		},
		activeActionRef() {
			return this.$store.state.actions.selectedActionRef;
		},
		isDirty() {
			return JSON.stringify({
				status: this.statusFilter,
				dateRange: this.dateRange,
				groupVars: this.groupVars,
				assignee: this.assigneeFilter
			}) !== JSON.stringify({
				status: this.worklistStatusState,
				dateRange: this.worklistDateRange,
				groupVars: this.worklistGroupVars,
				assignee: this.worklistAssignee
			});
		}
	},
	watch: {
		worklistExecutionType() {
			this.fetchActions();
		},
		'userInterfaceDetails.custom_field_definitions': {
			handler() {
				this.columnDefs = this.buildColumnDefs();
			}
		},
		availableActions: {
			handler(newVal, oldVal) {
				if (!oldVal || !this.gridApi) return;
				this.gridApi.setGridOption('rowData', newVal);
			},
			immediate: false
		},
		// Redraw affected rows when active action changes (highlight)
		activeActionRef(newRef, oldRef) {
			if (!this.gridApi) return;
			const nodesToRedraw: any[] = [];
			if (oldRef) {
				const oldNode = this.gridApi.getRowNode(oldRef);
				if (oldNode) nodesToRedraw.push(oldNode);
			}
			if (newRef) {
				const newNode = this.gridApi.getRowNode(newRef);
				if (newNode) nodesToRedraw.push(newNode);
			}
			if (nodesToRedraw.length) {
				this.gridApi.redrawRows({ rowNodes: nodesToRedraw });
			}
		}
	},
	created() {
		this.$store.commit('actions/SET_SELECTED_ACTION_REF', null);

		this.statusFilter = [...(this.worklistStatusState || [])];
		this.assigneeFilter = this.worklistAssignee;
		this.dateRange = this.worklistDateRange ? [...this.worklistDateRange] : undefined;
		this.groupVars = { ...this.worklistGroupVars };

		this.columnDefs = this.buildColumnDefs();
		this.initialRowData = this.availableActions;
		this.fetchActions();
	},
	methods: {
		isBulkExecutableAction(action: any) {
			return action?.execution_type !== 'MANUAL' && action?.channel !== 'PHONE';
		},
		onRowClicked(params: any) {
			if (params.node.group) {
				params.node.setExpanded(!params.node.expanded);
				return;
			}
			if (!params.data) return;
			// Skip checkbox column, attachments, and recipient link
			const target = params.event?.target;
			if (target?.closest('.ag-checkbox, .ag-selection-checkbox, .action-attachment-button, .action-table-recipient-link')) return;
			this.openActionDetail(params.data);
		},
		onGridReady(params: any) {
			this.gridApi = params.api;
			if (this.availableActions.length > 0) {
				this.gridApi.setGridOption('rowData', this.availableActions);
			}
		},
		getRowId(params: any) {
			return params.data?.reference;
		},
		getRowClass(params: any) {
			if (params.data?.reference && params.data.reference === this.activeActionRef) {
				return 'ag-row--active';
			}
			return '';
		},
		/** Walk grid visual order and build queue with group paths */
		buildActionQueue() {
			if (!this.gridApi) return [];
			const queue: Array<{ ref: string; groupPath: string[] }> = [];
			this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
				if (node.group || !node.data) return;
				// Collect group path from parent chain
				const groupPath: string[] = [];
				let parent = node.parent;
				while (parent && parent.level >= 0) {
					if (parent.key != null) groupPath.unshift(parent.key);
					parent = parent.parent;
				}
				queue.push({ ref: node.data.reference, groupPath });
			});
			return queue;
		},
		buildColumnDefs() {
			const _this = this;
			const customFieldColumns = this.buildCustomFieldColumns();
			return [
				{
					checkboxSelection: (params) => {
						return _this.isBulkExecutableAction(params.data);
					},
					width: 50,
					pinned: 'left',
					lockPosition: true,
					suppressMenu: true,
					cellRenderer: (params) => {
						if (!params.data) return '';
						if (params.data?.execution_type !== 'MANUAL' && params.data?.channel !== 'PHONE') {
							return ''; // Let AG Grid render the native checkbox
						}
						return `<div style="cursor: help; position: absolute; top: 0; left: 0; width: 50px; height: 100%; display: flex; align-items: center; justify-content: center;"><i class="el-icon-warning-outline" style="left: 12px; position: absolute;"></i></div>`;
					},
					tooltipValueGetter: (params) => {
						if (!params.data) return '';
						if (params.data?.execution_type === 'MANUAL') {
							return _this.$t('validator.action.general.manual_cannot_be_executed_in_bulk');
						}
						if (params.data?.channel === 'PHONE') {
							return _this.$t('validator.action.phone.cannot_be_executed_in_bulk');
						}
						return '';
					},
					tooltipShowDelay: 0
				},
				{
					field: 'start_time',
					headerName: this.$t('planned_date'),
					width: 120,
					sortable: true,
					filter: false, // Server-side filtered via date range picker
					valueFormatter: (params) => params.value ? parseTime(params.value, '{d}-{m}-{y}') : '',
					cellRenderer: (params) => {
						const displayValue = params.valueFormatted;
						return `<span class="action-table-link">${displayValue}</span>`;
					},
					onCellClicked: (params) => {
						if (!params.data) return;
						const target = params.event?.target;
						if (!target?.closest('.action-table-link')) return;
						_this.openActionDetail(params.data);
					}
				},
				{
					field: 'executed_at',
					headerName: this.$t('last_updated'),
					width: 120,
					sortable: true,
					filter: false, // Server-side filtered via date range picker (status-dependent logic)
					valueGetter: (params) => params.data?.executed_at || params.data?.canceled_at || '',
					valueFormatter: (params) => params.value ? parseTime(params.value, '{d}-{m}-{y}') : ''
				},
				{
					field: 'message_code',
					headerName: 'Type',
					width: 180,
					sortable: true,
					filter: true,
					valueGetter: (params) => {
						if (!params.data) return '';
						const code = _this.$translate.action_message_code(params.data.message_code);
						return params.data.is_retry ? `${code} ↺` : code;
					},
					enableRowGroup: true,
					rowGroup: true,
					rowGroupIndex: 0
				},
				{
					field: 'channel',
					headerName: this.$t('channel'),
					width: 100,
					sortable: true,
					filter: true,
					valueFormatter: (params) => params.value ? _this.$translate.channel(params.value) : '',
					enableRowGroup: true,
					rowGroup: true,
					rowGroupIndex: 1
				},
				{
					field: 'recipient_by_reference',
					headerName: this.$tc('recipient', 1),
					width: 300,
					sortable: true,
					filter: true,
					valueGetter: (params) => {
						if (!params.data) return '-';
						const ref = params.data.recipient_by_reference;
						if (ref?.name && ref?.account_id) {
							return `${ref.name} (${ref.account_id})`;
						}
						return ref?.name || ref?.account_id || '-';
					},
					filterValueGetter: (params) => {
						if (!params.data) return '';
						const ref = params.data.recipient_by_reference;
						return `${ref?.name || ''} ${ref?.account_id || ''}`;
					},
					cellRenderer: (params) => {
						if (!params.data) return params.value || '-';
						const ref = params.data.recipient_by_reference;
						if (!ref?.reference) return params.value || '-';
						const displayValue = params.value || '-';
						return `
							<span class="action-table-recipient-link">
								${displayValue}
								<svg class="svg-icon action-table-icon" aria-hidden="true">
									<use href="#icon-up-right-from-square" />
								</svg>
							</span>
						`;
					},
					onCellClicked: (params) => {
						if (!params.data) return;
						const target = params.event?.target;
						if (!target?.closest('.action-table-recipient-link')) return;
						const ref = params.data.recipient_by_reference;
						if (!ref?.reference) return;
						Utils.openInNewTab({ name: 'recipient-detail', params: { id: ref.reference, participant: params.data.participant } });
					}
				},
				{
					field: 'document_number',
					headerName: this.$t('document'),
					width: 150,
					sortable: true,
					filter: true,
					valueGetter: (params) => {
						if (!params.data) return '';
						return params.data.primary_invoice_by_reference?.invoice_number || params.data.credit_note_by_reference?.credit_note_number || '';
					},
					filterValueGetter: (params) => {
						if (!params.data) return '';
						return params.data.primary_invoice_by_reference?.invoice_number || params.data.credit_note_by_reference?.credit_note_number || '';
					}
				},
				{
					field: 'amount',
					headerName: this.$t('totalAmount'),
					width: 150,
					sortable: true,
					filter: 'agNumberColumnFilter',
					enableValue: true,
					aggFunc: 'sum',
					valueGetter: (params) => {
						if (!params?.data) {
							return null;
						}
						return params.data.amount ?? null;
					},
					valueFormatter: (params) => {
						if (params.value == null) {
							return '';
						}
						return _this.$formatter.toCurrency(params.value, params.data?.currency);
					},
					comparator: (a, b) => {
						const numA = a ?? 0;
						const numB = b ?? 0;
						return numA - numB;
					}
				},
				{
					field: 'status',
					headerName: this.$t('status'),
					width: 100,
					sortable: true,
					filter: false, // Server-side filtered via status el-select
					enableRowGroup: true,
					valueFormatter: (params) => params.value ? _this.$translate.action_status(params.value) : ''
				},
				{
					field: 'assignee',
					headerName: this.$t('assignee'),
					width: 140,
					sortable: true,
					enableRowGroup: true,
					valueGetter: (params) => {
						if (!params.data) return '';
						if (!params.data.assignee) return _this.$t('WorklistTable.unassigned');
						const user = _this.participantUsers?.find(u => u.person_by_reference.reference === params.data.assignee);
						return user?.person_by_reference?.username || params.data.assignee;
					}
				},
				{
					colId: 'attachments',
					headerName: this.$t('InvoiceDisputesTable.attachments'),
					width: 140,
					sortable: false,
					filter: false,
					valueGetter: (params) => {
						if (!params.data) return 0;
						return _this.getCombinedAttachments(params.data).length;
					},
					cellRenderer: (params) => {
						if (!params.data) return '';
						const attachmentsCount = Number(params.value) || 0;
						let html = '<button type="button" class="el-button el-button--text action-attachment-button" style="padding: 0; border: none; background: none; cursor: pointer;">';
						html += '<span>';

						if (attachmentsCount > 0) {
							html += `<span>${_this.$tc('number_of_attachments', attachmentsCount, { count: attachmentsCount })}</span>`;
						} else {
							html += `<span><i class="el-icon-plus"></i>&nbsp;${_this.$t('invoice-disputes.no_attachments')}</span>`;
						}

						html += '</span>';
						html += '</button>';
						return html;
					},
					onCellClicked: (params) => {
						if (!params.data) return;
						params.event.preventDefault();
						params.event.stopPropagation();
						_this.handleAttachments(params.data);
						return false;
					}
				},
				{
					field: 'legal_entity_by_reference.registration_name',
					headerName: this.$t('legalEntity'),
					width: 180,
					sortable: true,
					filter: true,
					enableRowGroup: true
				},
				...customFieldColumns
			];
		},
		buildCustomFieldColumns() {
			if (!this.userInterfaceDetails?.custom_field_definitions) {
				return [];
			}
			return this.userInterfaceDetails.custom_field_definitions.slice(0, 5).map(definition => ({
				field: `custom_fields.${definition.path}`,
				headerName: getCustomFieldLabel(definition, this.$i18n.locale),
				width: 160,
				sortable: true,
				filter: getAgGridFilterType(definition.type),
				valueGetter: (params) => params.data ? getCustomFieldValue(params.data.custom_fields, definition.path, definition.type) : null,
				valueFormatter: (params) => formatCustomFieldValue(params.value, definition.type)
			}));
		},
		openActionDetail(row) {
			if (!row) return;

			// Build queue scoped to the clicked row's group
			const fullQueue = this.buildActionQueue();
			const clickedItem = fullQueue.find(item => item.ref === row.reference);
			const groupKey = JSON.stringify(clickedItem?.groupPath || []);
			const queue = fullQueue.filter(item => JSON.stringify(item.groupPath) === groupKey);
			const index = queue.findIndex(item => item.ref === row.reference);
			this.$store.commit('worklist/SET_ACTION_QUEUE', queue);
			this.$store.commit('worklist/SET_ACTION_QUEUE_INDEX', index >= 0 ? index : 0);

			if (row.channel === 'PHONE') {
				this.$store.commit('risolto/SET_ACTION_TABLE_VISIBLE', false);
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayAction', row.reference);
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayVisible', true);
				this.$router.push({
					name: 'recipient-detail',
					params: { id: row.recipient, participant: row.participant }
				});
			} else {
				this.$store.commit('actions/SET_SELECTED_ACTION_REF', row.reference);
				this.$store.commit('risolto/SET_SELECTED_RECIPIENT', row.recipient_by_reference);
				this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', row.channel);
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
			}
		},
		handleSelectionChange(event) {
			this.selectedActions = event.api.getSelectedRows();
		},
		fetchActions() {
			if (this.loading || !this.selectedParticipantStore) return;

			this.showLimitWarning = false;
			this.actualCount = 0;
			this.loading = true;

			const statusArray = this.worklistStatusState || [];
			let executionTypes;
			switch (this.worklistExecutionType) {
				case 'AUTOMATED':
					executionTypes = `{"AUTOMATED"}`;
					break;
				case 'SEMI_AUTOMATED':
					executionTypes = `{"SEMI_AUTOMATED"}`;
					break;
				case 'MANUAL':
					executionTypes = this.worklistGroupVars.showManualActions
						? `{"MANUAL", "SEMI_AUTOMATED"}`
						: `{"SEMI_AUTOMATED"}`;
					break;
				default:
					executionTypes = this.worklistGroupVars.showManualActions
						? `{"MANUAL", "AUTOMATED", "SEMI_AUTOMATED"}`
						: `{"SEMI_AUTOMATED", "AUTOMATED"}`;
			}

			const variables: any = {
				participant: this.selectedParticipantStore,
				legalEntity: this.selectedLegalEntityStore || null,
				recipient: null,
				assignee: this.worklistAssignee || null,
				statuses: `{${statusArray.join(',')}}`,
				executionTypes: executionTypes,
				dateFrom: this.worklistDateRange ? this.worklistDateRange[0].concat('T00:00:00.000Z') : null,
				dateTo: this.worklistDateRange ? this.worklistDateRange[1].concat('T23:59:59.999Z') : null,
				limit: this.limit
			};

			Logging.log('WorklistActionTable.fetchActions - variables: ', variables);

			this.$apollo
				.query({
					query: this.makeActionsQuery(),
					variables: variables
				})
				.then(result => {
					if (result.data) {
						const actions = result.data.availableActions.map((item: any) => item.data);
						this.actualCount = actions.length;
						this.showLimitWarning = actions.length === this.limit;
						this.$store.commit('actions/UPSERT_ACTIONS', actions);
						const refs = actions.map(a => a.reference);
						this.$store.commit('worklist/SET_WORKLIST_ACTION_REFS', refs);
						this.loading = false;
					}
				})
				.catch(e => {
					Logging.error('Error: ', e);
					this.$store.commit('worklist/SET_WORKLIST_ACTION_REFS', []);
					this.loading = false;
				});
		},
		handleShowManualActionsChange(value) {
			this.groupVars = { ...this.groupVars, showManualActions: value };
		},
		handleRefreshList() {
			this.fetchActions();
		},
		async handleFilter() {
			this.applyFilters();
			await this.fetchActions();
		},
		handleReset() {
			this.limit = 1000;
			this.actualCount = 0;
			this.statusFilter = ['OPEN'];
			this.assigneeFilter = null;
			this.dateRange = undefined;
			this.groupVars = { ...this.groupVars, showManualActions: true };
			this.applyFilters();
			this.fetchActions();

			// Reset AG Grid state (columns, grouping, sorting, filters, pivot)
			if (this.gridApi) {
				this.gridApi.resetColumnState();
				this.gridApi.setFilterModel(null);
				this.gridApi.setGridOption('pivotMode', false);
			}
		},
		undoChanges() {
			this.statusFilter = [...(this.worklistStatusState || [])];
			this.assigneeFilter = this.worklistAssignee;
			this.dateRange = this.worklistDateRange ? [...this.worklistDateRange] : undefined;
			this.groupVars = { ...this.worklistGroupVars };
		},
		// Commit filter values to Vuex
		applyFilters() {
			this.$store.commit('worklist/SET_WORKLIST_STATUS_STATE', this.statusFilter);
			this.$store.commit('worklist/SET_WORKLIST_ASSIGNEE', this.assigneeFilter);
			this.$store.commit('worklist/SET_WORKLIST_DATE_RANGE', this.dateRange);
			this.$store.commit('worklist/SET_WORKLIST_GROUPVARS', this.groupVars);
		},
		increaseLimitAndFetch() {
			this.limit = Math.min(this.limit * 2, this.maxLimit);
			this.fetchActions();
		},
		handleExport(command: string) {
			const fileName = `${this.$t('routerTitles.Worklist')} ${new Date().toISOString().slice(0, 10)}`;
			if (command === 'csv') {
				this.gridApi.exportDataAsCsv({ fileName });
			} else if (command === 'excel') {
				this.gridApi.exportDataAsExcel({ fileName });
			}
		},
		handleSelectedActions() {
			const actionsList = this.selectedActions
				.filter(action => this.isBulkExecutableAction(action))
				.map(action => action.reference);

			if (actionsList.length === 0) {
				this.$notify({ title: this.$t('validator.action.general.manual_cannot_be_executed_in_bulk'), type: 'warning' });
				return Promise.resolve();
			}
			const payload = {
				version: 'v1',
				participant: this.selectedParticipantStore,
				actions: actionsList,
				dry_run: false,
				force_send: this.$store.getters['forceSendStore']
			};

			this.executing = true;

			return axios
				.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/confirm`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				})
				.then(() => {
					this.executing = false;
					this.$store.commit('actions/UPSERT_ACTIONS', this.selectedActions.map(a => ({ ...a, status: 'DONE' })));
					this.$notify({ title: this.$tc('actionExecuted', this.selectedActions.length), type: 'success' });
				})
				.catch(e => {
					this.executing = false;
					Logging.error('Err in handleSelectedActions: ', e.response?.data);
					this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
				});
		},
		async forceOpenSelectedActions() {
			this.$confirm(this.$t('can_not_be_undone'), 'Warning', {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel')
			})
				.then(async () => {
					this.executing = true;
					try {
						await actionForceOpen(this.selectedParticipantStore, this.selectedActions);
						this.$store.commit('actions/UPSERT_ACTIONS', this.selectedActions.map(a => ({ ...a, status: 'OPEN' })));
						this.$notify({ title: this.$tc('actionOpened', this.selectedActions.length), type: 'success' });
					} catch (e) {
						this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
					} finally {
						this.executing = false;
					}
				})
				.catch(() => {}); // User cancelled — do nothing
		},
		async forceCancelSelectedActions() {
			this.$confirm(this.$t('can_not_be_undone'), 'Warning', {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel')
			})
				.then(async () => {
					this.executing = true;
					try {
						await actionForceCancel(this.selectedParticipantStore, this.selectedActions);
						this.$store.commit('actions/UPSERT_ACTIONS', this.selectedActions.map(a => ({ ...a, status: 'CANCELED' })));
						this.$notify({ title: this.$tc('actionCanceled', this.selectedActions.length), type: 'success' });
					} catch (e) {
						this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
					} finally {
						this.executing = false;
					}
				})
				.catch(() => {}); // User cancelled — do nothing
		},
		cancelSelectedActions() {
			const actionsList = this.selectedActions.map(el => el.reference);
			const payload = {
				version: 'v1',
				participant: this.selectedParticipantStore,
				actions: actionsList,
				reason: ''
			};

			this.executing = true;

			return axios
				.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/cancel`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				})
				.then(() => {
					this.executing = false;
					this.$store.commit('actions/UPSERT_ACTIONS', this.selectedActions.map(a => ({ ...a, status: 'CANCELED' })));
					this.$notify({ title: this.$tc('actionCanceled', this.selectedActions.length), type: 'success' });
				})
				.catch(e => {
					this.executing = false;
					Logging.error('Err in cancelSelectedActions: ', e);
					this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
				});
		},
		async handleAttachments(row) {
			// Always fetch full action — list only has partial metadata
			this.attachmentsDialogAction = null;
			this.attachmentsDialogFetching = true;
			this.attachmentsDialogVisible = true;

			try {
				const fetchedAction = await this.$store.dispatch('actions/fetchActionDetails', row.reference);
				this.attachmentsDialogAction = fetchedAction;
			} catch (e) {
				Logging.error('Error fetching action details for attachments dialog:', e);
				this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
			} finally {
				this.attachmentsDialogFetching = false;
			}
		},
		async handleAttachmentUpload(attachment: S3_Attachment) {
			if (!this.attachmentsDialogAction) return;

			const metadata = { ...this.attachmentsDialogAction.metadata || {} };
			metadata.attachments = [...(metadata.attachments || []), attachment];

			const updatedAction = { ...this.attachmentsDialogAction, metadata };
			this.attachmentsDialogAction = updatedAction;
			this.$store.commit('actions/UPSERT_ACTION', updatedAction);

			try {
				await actionUpdateMetadata(updatedAction.reference, metadata, updatedAction.channel);
			} catch (e) {
				Logging.error('Error updating attachment:', e);
				this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
			}
		},
		async handleAttachmentRemove(attachment: S3_Attachment) {
			if (!this.attachmentsDialogAction) return;

			const metadata = { ...this.attachmentsDialogAction.metadata || {} };
			metadata.attachments = (metadata.attachments || []).filter((a: S3_Attachment) => a.key !== attachment.key);

			if (metadata.channels?.[0]?.attachments) {
				metadata.channels = [{ ...metadata.channels[0], attachments: metadata.channels[0].attachments.filter((a: S3_Attachment) => a.key !== attachment.key) }];
			}

			const updatedAction = { ...this.attachmentsDialogAction, metadata };
			this.attachmentsDialogAction = updatedAction;
			this.$store.commit('actions/UPSERT_ACTION', updatedAction);

			try {
				await actionUpdateMetadata(updatedAction.reference, metadata, updatedAction.channel);
			} catch (e) {
				Logging.error('Error removing attachment:', e);
				this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
			}
		},
		getCombinedAttachments(action) {
			if (!action?.metadata) {
				return [];
			}

			return [
				...(action.metadata.attachments || []),
				...(action.metadata.channels?.[0]?.attachments || [])
			];
		},
		makeActionsQuery() {
			return gql`
				query getActionsListMinimal(
					$participant: uuid!
					$legalEntity: uuid
					$recipient: uuid
					$assignee: String
					$statuses: _text
					$executionTypes: _text
					$dateFrom: timestamptz
					$dateTo: timestamptz
					$limit: Int!
				) {
					availableActions: risolto_fe_action_list_v3(
						args: {
							p_participant: $participant
							p_legal_entity: $legalEntity
							p_recipient: $recipient
							p_assignee: $assignee
							p_statuses: $statuses
							p_execution_types: $executionTypes
							p_date_from: $dateFrom
							p_date_to: $dateTo
							p_limit: $limit
						}
					) {
						data
						participant
					}
				}
			`;
		}
	}
};
